/**
 * @module features/client-device-management/components/ActiveClientsList
 * @description Adaptive active-clients listing displaying strictly Client Name (ID underneath),
 * Logo, Contact Person name, City & State, and a "Manage" action button.
 * Implements container-aware responsive tables with sticky headers on desktop/tablet
 * and thumb-friendly stacked cards on mobile (<640px), plus geometry-matched skeletons.
 */
import * as React from "react";
import { ArrowRight } from "lucide-react";
import { ClientSummary } from "@/features/clients";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ClientLogoAvatar } from "./ClientLogoAvatar";

export interface ActiveClientsListProps {
  clients: ClientSummary[];
  onManage: (client: ClientSummary) => void;
}

/**
 * Renders the active client directory as a semantic sticky-header data table on
 * tablet/desktop containers and clean stacked summary cards on mobile screens.
 */
export function ActiveClientsList({ clients, onManage }: ActiveClientsListProps) {
  const captionId = React.useId();

  return (
    <div className="@container w-full">
      {/* Tablet & Desktop Semantic Data Table (container >=640px) */}
      <div
        tabIndex={0}
        role="region"
        aria-labelledby={captionId}
        className="hidden @[640px]:block rounded-2xl border border-border/80 bg-card shadow-card overflow-auto max-h-[min(680px,75dvh)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <table className="w-full border-separate border-spacing-0 text-sm tabular-nums">
          <caption id={captionId} className="sr-only">
            Active client organizations directory for device management
          </caption>
          <thead className="sticky top-0 z-sticky bg-card/95 backdrop-blur-sm">
            <tr>
              <th
                scope="col"
                className="sticky left-0 z-sticky bg-card/95 h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Client
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Contact Person
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-left align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                City &amp; State
              </th>
              <th
                scope="col"
                className="h-11 px-4 text-right align-middle text-xs font-medium text-muted-foreground border-b border-border/80"
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr
                key={client.id}
                className="group transition-colors hover:bg-muted/40"
              >
                {/* Row Header: Logo + Client Name + ID underneath */}
                <th
                  scope="row"
                  className="sticky left-0 bg-card group-hover:bg-muted/40 transition-colors px-4 py-3 text-left align-middle font-normal border-b border-border/60 last:border-b-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ClientLogoAvatar
                      orgName={client.org_name}
                      logoUrl={client.logo_url}
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-foreground truncate max-w-[260px] lg:max-w-[340px]">
                        {client.org_name}
                      </p>
                      <p
                        className="font-mono text-[11px] leading-4 text-muted-foreground truncate select-all mt-0.5"
                        title={client.id}
                      >
                        {client.id}
                      </p>
                    </div>
                  </div>
                </th>

                {/* Contact Person Name */}
                <td className="px-4 py-3 align-middle text-[13px] leading-[18px] text-foreground border-b border-border/60">
                  <span className="truncate block max-w-[200px]">
                    {client.contact_person_name}
                  </span>
                </td>

                {/* City & State */}
                <td className="px-4 py-3 align-middle text-[13px] leading-[18px] text-muted-foreground border-b border-border/60">
                  <span className="truncate block max-w-[220px]">
                    {client.city}, {client.state}
                  </span>
                </td>

                {/* Action: Manage Button */}
                <td className="px-4 py-3 align-middle text-right border-b border-border/60">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onManage(client)}
                    aria-label={`Manage devices for ${client.org_name}`}
                    className="h-9 touch:min-h-[44px] px-3.5 text-xs gap-1.5"
                  >
                    <span>Manage</span>
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Stacked Cards (container <640px) */}
      <div className="grid grid-cols-1 gap-3 @[640px]:hidden" role="list">
        {clients.map((client) => (
          <div
            key={client.id}
            role="listitem"
            className="rounded-2xl border border-border/80 bg-card p-4 shadow-card space-y-3"
          >
            {/* Logo + Client Name + ID underneath */}
            <div className="flex items-center gap-3 min-w-0">
              <ClientLogoAvatar
                orgName={client.org_name}
                logoUrl={client.logo_url}
                size="md"
              />
              <div className="min-w-0 flex-1">
                <h3 className="font-medium text-sm text-foreground truncate">
                  {client.org_name}
                </h3>
                <p className="font-mono text-[11px] leading-4 text-muted-foreground truncate select-all mt-0.5">
                  {client.id}
                </p>
              </div>
            </div>

            {/* Contact Person & City/State */}
            <dl className="grid grid-cols-2 gap-2 pt-2.5 border-t border-border/60 text-xs">
              <div className="min-w-0">
                <dt className="text-[11px] text-muted-foreground">Contact Person</dt>
                <dd className="font-medium text-foreground truncate mt-0.5">
                  {client.contact_person_name}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[11px] text-muted-foreground">City &amp; State</dt>
                <dd className="text-foreground truncate mt-0.5">
                  {client.city}, {client.state}
                </dd>
              </div>
            </dl>

            {/* Full-width touch-friendly Manage CTA (>=44px touch target) */}
            <div className="pt-1">
              <Button
                type="button"
                size="sm"
                onClick={() => onManage(client)}
                aria-label={`Manage devices for ${client.org_name}`}
                className="w-full min-h-[44px] text-xs gap-1.5"
              >
                <span>Manage</span>
                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Geometry-matched loading skeleton for ActiveClientsList ensuring CLS = 0.00
 * across Desktop, Tablet, and Mobile viewports.
 */
export function ActiveClientsSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      className="@container w-full"
      aria-busy="true"
      aria-label="Loading active clients"
    >
      {/* Desktop/Tablet Table Skeleton */}
      <div className="hidden @[640px]:block rounded-2xl border border-border/80 bg-card shadow-card overflow-hidden">
        <div className="h-11 px-4 border-b border-border/80 flex items-center justify-between bg-muted/20">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-3.5 w-16" />
        </div>
        <div className="divide-y divide-border/60">
          {Array.from({ length: rows }).map((_, idx) => (
            <div
              key={idx}
              className="px-4 py-3 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
                <div className="space-y-1.5 flex-1 max-w-[220px]">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-44" />
                </div>
              </div>
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-9 w-24 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Cards Skeleton */}
      <div className="grid grid-cols-1 gap-3 @[640px]:hidden">
        {Array.from({ length: 4 }).map((_, idx) => (
          <div
            key={idx}
            className="rounded-2xl border border-border/80 bg-card p-4 shadow-card space-y-3"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl shrink-0" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-44" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-2.5 border-t border-border/60">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
            <Skeleton className="h-11 w-full rounded-md" />
          </div>
        ))}
      </div>
    </div>
  );
}
